import { useEffect, useRef, useState } from "react";
import api from "@/api/api.js";
import i18n from "@/i18n.js";
import $ from "jquery";
import "datatables.net";
import languageES from "datatables.net-plugins/i18n/es-MX";
import CompanyFormModal from "@/pages/shared/modal_forms/CompanyFormModal.jsx";
import { Button } from "react-bootstrap";
import Swal from "sweetalert2";
import { ROLES } from "@/config/roles.js";
import { deliverFiscalDocument } from "@/utils/deliverFiscalDocument.js";

const Companies = () => {
  const tableRef = useRef(null);
  const dataTable = useRef(null);
  // El emisor y el tipo que se están entregando: la tabla es de jQuery, así que
  // el botón no puede llevarlos consigo hasta que el archivo esté elegido.
  const pendingUpload = useRef(null);
  const fileInput = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);

  const userRoles = JSON.parse(sessionStorage.getItem("roles") || "[]");
  const isMaster = userRoles.includes(ROLES.SUPER_ADMIN);

  const handleOpenModal = (companyId = null) => {
    setSelectedCompanyId(companyId);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    if (dataTable.current) {
      dataTable.current.ajax.reload();
    }
  };

  const handleSave = () => {
    handleCloseModal();
  };

  const handlePickFile = (companyId, type) => {
    pendingUpload.current = { companyId, type };
    fileInput.current.value = "";
    fileInput.current.click();
  };

  const handleFileChosen = async (event) => {
    const file = event.target.files?.[0];
    const pending = pendingUpload.current;
    pendingUpload.current = null;
    if (!file || !pending) return;

    const delivered = await deliverFiscalDocument({ ...pending, file });
    if (delivered && dataTable.current) {
      dataTable.current.ajax.reload(null, false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: "No podrás revertir esta acción.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      customClass: {
        confirmButton: "btn btn-danger",
      },
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/companies/${id}`);
        Swal.fire("Eliminado", i18n.t("ui.issuerDeleted"), "success");
        if (dataTable.current) {
          dataTable.current.ajax.reload();
        }
      } catch (error) {
        console.error("Error deleting company:", error);
        Swal.fire("Error", i18n.t("ui.issuerDeleteError"), "error");
      }
    }
  };

  useEffect(() => {
    const columns = [
      { title: "Razón social / Nombre", data: "name" },
      { title: "RFC", data: "taxIdentification" },
      {
        title: "Tipo de persona",
        render: (data, type, row) => `Persona ${row["type"]}`,
      },
      {
        title: "Dueño",
        render: (data, type, row) => {
          if (!row?.user) return "—";
          const uid = row.user["@id"]?.split("/").pop();
          const label = row.user.username ?? "—";
          return uid
            ? `<a href="/client-users/${uid}/show">${label}</a>`
            : label;
        },
      },
    ];

    columns.push({
      title: "Facturas generadas",
      data: "generatedBillingsCount",
      className: "text-center",
      render: (data) => data ?? 0,
    });

    columns.push(
      {
        title: "Acciones",
        orderable: false,
        className: "text-nowrap",
        render: function (data, type, row) {
          const companyId = row["@id"].split("/").pop();
          const edit = isMaster
            ? `<button type="button" class="btn btn-outline-primary btn-edit-company" data-id="${companyId}" title="Editar"><i class="bi bi-pencil-square"></i></button>`
            : "";
          const del = isMaster
            ? `<button type="button" class="btn btn-outline-danger btn-delete-company" data-id="${companyId}" title="Eliminar"><i class="bi bi-trash"></i></button>`
            : "";
          // Se entregan desde aquí sin entrar a la ficha: es la pantalla donde el
          // contador recorre a sus emisores uno tras otro.
          const constancia = `<button type="button" class="btn btn-outline-secondary btn-upload-document" data-id="${companyId}" data-type="fiscalCertificate" title="${i18n.t("ui.uploadFiscalCertificate")}"><i class="bi bi-file-earmark-arrow-up"></i></button>`;
          const opinion = `<button type="button" class="btn btn-outline-secondary btn-upload-document" data-id="${companyId}" data-type="complianceOpinion" title="${i18n.t("ui.uploadComplianceOpinion")}"><i class="bi bi-shield-fill-plus"></i></button>`;
          return `<div class="btn-group btn-group-sm" role="group" aria-label="${i18n.t("ui.issuerActions")}">
                      ${edit}
                      <a href="/${companyId}/detailCompany" class="btn btn-outline-info btn-detail-company" title="Ver detalle">
                        <i class="bi bi-eye"></i>
                      </a>
                      ${constancia}
                      ${opinion}
                      ${del}
                    </div>`;
        },
      },
      { title: "Última actualización", data: "createdAtAgo" },
    );

    dataTable.current = $(tableRef.current).DataTable({
      processing: true,
      serverSide: true,
      language: languageES,
      order: [[0, "asc"]],
      ajax: async (dataTablesParams, callback) => {
        try {
          const response = await api.get("/companies", {
            params: {
              page: dataTablesParams.start / dataTablesParams.length + 1,
              itemsPerPage: dataTablesParams.length,
              search: dataTablesParams.search.value,
            },
          });

          callback({
            data: response.data["hydra:member"],
            recordsTotal: response.data["hydra:totalItems"],
            recordsFiltered: response.data["hydra:totalItems"],
          });
        } catch (error) {
          console.error("Error al cargar datos:", error);
          callback({ data: [] });
        }
      },
      columns,
    });

    $(tableRef.current).on("click", ".btn-edit-company", function () {
      const companyId = $(this).data("id");
      handleOpenModal(companyId);
    });

    $(tableRef.current).on("click", ".btn-delete-company", function () {
      const companyId = $(this).data("id");
      handleDelete(companyId);
    });

    $(tableRef.current).on("click", ".btn-upload-document", function () {
      handlePickFile($(this).data("id"), $(this).data("type"));
    });

    return () => {
      if (dataTable.current) {
        dataTable.current.destroy();
      }
    };
  }, []);

  return (
    <>
      <div className="col">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center pb-3">
              <h5 className="card-title mb-0">{i18n.t("page.issuerList")}</h5>
              {isMaster && (
                <Button variant="primary" onClick={() => handleOpenModal()}>
                  <i className="bi bi-plus-lg me-1"></i>
                  {i18n.t("ui.createIssuer")}
                </Button>
              )}
            </div>
            <input
              type="file"
              accept="application/pdf"
              ref={fileInput}
              onChange={handleFileChosen}
              className="d-none"
            />
            <div className="table-responsive">
              <table
                ref={tableRef}
                className="display"
                style={{ width: "100%" }}
              ></table>
            </div>

            {isMaster && (
              <CompanyFormModal
                show={showModal}
                handleClose={handleCloseModal}
                companyId={selectedCompanyId}
                onSave={handleSave}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Companies;
